import styled, { keyframes, css } from "styled-components";

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const popIn = keyframes`
  from { opacity: 0; transform: translateY(20px) scale(0.95); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

export const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(6, 7, 10, 0.7);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
  animation: ${fadeIn} 0.18s ease-out;
`;

export const Panel = styled.div`
  background: var(--panel);
  border: 1px solid var(--border-strong);
  border-radius: 16px;
  padding: 32px;
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6);
  animation: ${popIn} 0.22s cubic-bezier(0.34, 1.56, 0.64, 1);
`;

export const Title = styled.div`
  font-family: 'JetBrains Mono', monospace;
  font-size: 14px;
  letter-spacing: 6px;
  color: var(--text-dim);
  text-transform: uppercase;
  text-align: center;
  margin-bottom: 4px;
`;

export const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  background: var(--bg-soft);
  border: 1px solid var(--border);
  border-radius: 10px;
`;

export const Label = styled.div`
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  letter-spacing: 3px;
  color: var(--text-dim);
  text-transform: uppercase;
`;

export const Toggle = styled.button`
  border: 1px solid ${(p) => (p.$on ? "var(--accent)" : "var(--border)")};
  background: ${(p) => (p.$on ? "rgba(94, 234, 212, 0.15)" : "var(--panel-2)")};
  color: ${(p) => (p.$on ? "var(--accent)" : "var(--text-dim)")};
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 3px;
  padding: 8px 18px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;

  ${(p) => p.$on && css`
    box-shadow: 0 0 12px var(--accent-glow);
  `}

  &:hover {
    transform: translateY(-1px);
  }
`;

export const Slider = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  justify-content: flex-end;

  input[type="range"] {
    flex: 1;
    max-width: 160px;
    accent-color: var(--accent);
    cursor: pointer;

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }
`;

export const SliderValue = styled.div`
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  font-weight: 700;
  color: var(--text);
  min-width: 42px;
  text-align: right;
`;

export const TestButton = styled.button`
  border: 1px solid var(--border);
  background: var(--panel-2);
  color: var(--text);
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 2px;
  padding: 8px 18px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    border-color: var(--accent);
    color: var(--accent);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const LangGroup = styled.div`
  display: flex;
  gap: 6px;
`;

export const LangChip = styled.button`
  border: 1px solid ${(p) => (p.$on ? "var(--accent)" : "var(--border)")};
  background: ${(p) => (p.$on ? "rgba(94, 234, 212, 0.15)" : "var(--panel-2)")};
  color: ${(p) => (p.$on ? "var(--accent)" : "var(--text-dim)")};
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 2px;
  padding: 8px 14px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  min-width: 48px;

  ${(p) => p.$on && css`
    box-shadow: 0 0 12px var(--accent-glow);
  `}

  &:hover {
    transform: translateY(-1px);
  }
`;

export const Close = styled.button`
  margin-top: 8px;
  border-radius: 10px;
  border: 1px solid var(--accent);
  background: linear-gradient(180deg, rgba(94, 234, 212, 0.18), rgba(94, 234, 212, 0.05));
  color: var(--accent);
  font-family: 'Space Grotesk', sans-serif;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 3px;
  text-transform: uppercase;
  padding: 12px 24px;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: 0 0 14px var(--accent-glow);

  &:hover {
    transform: translateY(-2px);
  }
`;

export const GearButton = styled.button`
  position: absolute;
  top: 24px;
  right: 24px;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: var(--panel);
  border: 1px solid var(--border);
  color: var(--text-dim);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 5;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
    transform: rotate(45deg);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;
